import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid, Stack, Typography } from '@mui/material';
import { useTranslations } from 'hooks/useTranslations';
import { useAncillaries } from 'pages/AncillariesPage/hooks';
import { EDictionaryTypes } from 'models/dictionary';
import { useLang } from 'models/langContext';
import { ERouteLinks } from 'models/route';
import { DetailsTemplate, EDetailsPageTypes } from 'components/templates/DetailsTemplate';
import { EButtonSizes, EButtonSizesPX, EButtonVariants } from 'constants/Buttons';
import { Chips } from 'components/atoms/Chips';
import { getFieldsByAmmunitionType } from 'pages/AncillariesPage/helpers';
import { EArtAmmunitionType, EArtAncillaryTypeName } from 'constants/ArtAccessoryType';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { formatDate } from 'utils/date';
import { EAmmunitionLabels } from 'pages/AncillariesPage/AmmunitionPage/DetailAmmunition';
import {
  AmmunitionInformationContainer,
  AmmunitionInformationTitle,
} from 'pages/AncillariesPage/AmmunitionPage/DetailAmmunition/styles';
import Link from '@mui/material/Link';
import Button from 'components/atoms/Button';
import { mapAncillaryTypeToTranslationKey } from 'utils/mapAncillaryTypeToTranslation';
import { renderDataColorByValues } from 'components/molecules/Table/helpers';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { IAmmunitionDetails, IAncillaryDetailsCustomfield } from 'models/ancillaries';
import { ECustomFieldType } from 'models/customField';
import { usePermission } from 'hooks/usePermission';
import { EPermission } from 'models/permissions';
import { omitNullishAndEmpty } from 'utils/omitNullishAndEmpty';
import { StyledLegalLink } from 'components/atoms/StyledLegalLink';
import { getLegalEntityType } from 'components/organisms/RegisteredFirearmDetails';
import { parseDictionary } from 'helpers/dictionary';
import { EMarkingTypes } from 'constants/Markings';
import { ETransactionTypes } from 'constants/TransactionTypes';

interface IAmmunitionDetailsProps {
  id: string;
  ammunition?: IAmmunitionDetails;
  visibleCustomFieldsData?: boolean;
  isForTransactionDetails?: boolean;
  initialTransaction?: boolean;
  marking?: string;
  transactionType?: ETransactionTypes;
  disableActions?: boolean;
  customFieldsData?: IAncillaryDetailsCustomfield[];
}

const AmmunitionDetails = ({
  id,
  ammunition,
  isForTransactionDetails = false,
  initialTransaction = false,
  marking,
  transactionType,
  visibleCustomFieldsData,
  disableActions,
  customFieldsData,
}: IAmmunitionDetailsProps) => {
  const { t } = useTranslations();
  const { selectedLanguage, defaultLanguage } = useLang();
  const { hasPermissions } = usePermission();
  const navigate = useNavigate();
  const { loadDictionaries, ammunitionDetail, getAmmunitionDetail, state } = useAncillaries();
  const { shortDateFormat } = useGlobalProperty();
  const [accessFields, setAccessFields] = useState<string[]>([]);
  const [showMarkings, setShowMarkings] = useState(false);
  const [ammunitionDetailsData, setAmmunitionDetailsData] = useState<
    IAmmunitionDetails | undefined
  >(undefined);

  useEffect(() => {
    if (ammunitionDetailsData) {
      if (ammunitionDetailsData.typeUniqueId) {
        const fields = getFieldsByAmmunitionType(
          ammunitionDetailsData.typeUniqueId as EArtAmmunitionType,
        );
        setAccessFields(fields);
      } else {
        setAccessFields([]);
      }
    }
  }, [ammunitionDetailsData]);

  useEffect(() => {
    (async () => {
      if (id && hasPermissions([EPermission.VIEW_LIMITED_ANCILLARIES])) {
        await getAmmunitionDetail(id, isForTransactionDetails);
      }
    })();
  }, [id]);

  useEffect(() => {
    if (!ammunition) {
      setAmmunitionDetailsData(ammunitionDetail);
      return;
    }

    setAmmunitionDetailsData({
      ...ammunitionDetail,
      ...omitNullishAndEmpty(ammunition),
    } as IAmmunitionDetails);
  }, [ammunitionDetail]);

  useEffect(() => {
    (async function initDictionaries() {
      await loadDictionaries([EDictionaryTypes.ArtAncillaryOptions, EDictionaryTypes.State]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  const showField = (fieldName: string) => accessFields.includes(fieldName);

  const renderCustomFieldData = (customField: IAncillaryDetailsCustomfield) => {
    if (customField.customFieldType === ECustomFieldType.BOOL) {
      if (customField.value === null) {
        return '-';
      }
      return customField.value
        ? t('customFields.value.yes.label')
        : t('customFields.value.no.label');
    }
    if (customField.customFieldType === ECustomFieldType.DATE) {
      return formatDate(customField.value, shortDateFormat);
    }
    return customField.value || '-';
  };

  // eslint-disable-next-line consistent-return
  const handleCustomFieldsData = () => {
    if (customFieldsData && customFieldsData.length) {
      return customFieldsData;
    }
    if (ammunitionDetailsData?.customFields) {
      return ammunitionDetailsData.customFields;
    }
  };

  return (
    <div>
      {ammunitionDetailsData && accessFields.length && (
        <Grid sx={{ gap: 3 }}>
          <DetailsTemplate
            title={ammunitionDetailsData?.type}
            pageType={EDetailsPageTypes.view}
            editPermissions={[EPermission.EDIT_ANCILLARY]}
            redirectToView={() => navigate(`${ERouteLinks.ViewAmmunition.replace(':id', id)}`)}
            disableActions={disableActions}
            actionButtonProps={{
              ...{ label: t('legalEntityDetails.ancillary.accordion.details.link') },
              ...{ size: 'small' },
              ...{
                sx: {
                  minWidth: '207px !important',
                  width: { xs: '100%', sm: '207px !important' },
                },
              },
              ...{ sizePX: EButtonSizesPX.px24 },
              ...{ variant: EButtonVariants.outlined },
            }}
          >
            <AmmunitionInformationContainer size={{ xs: 12 }} container>
              <Grid size={{ xs: 12 }}>
                <Stack>
                  <AmmunitionInformationTitle variant="subtitle1">
                    {t(EAmmunitionLabels.ammunitionGeneralInformation)}
                  </AmmunitionInformationTitle>
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(EAmmunitionLabels.ancillaryType)}
                  </Typography>
                  <Typography variant="body2">
                    {t(mapAncillaryTypeToTranslationKey(ammunitionDetailsData?.ancillaryType))}
                  </Typography>
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(EAmmunitionLabels.ammunitionType)}
                  </Typography>
                  <Typography variant="body2">
                    {emptyFieldHolder(ammunitionDetailsData?.type)}
                  </Typography>
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Typography
                    variant="subtitle2"
                    sx={{ marginBottom: '4px', textTransform: 'uppercase' }}
                  >
                    {t(EAmmunitionLabels.artId)}
                  </Typography>
                  <Typography variant="body2" style={{ textTransform: 'capitalize' }}>
                    {emptyFieldHolder(ammunitionDetailsData?.artId)}
                  </Typography>
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Link
                    href={`${ERouteLinks.ViewAncillaries.replace(
                      ':id',
                      ammunitionDetailsData.artUniqueId,
                    ).replace(':type', EArtAncillaryTypeName.AMMUNITION.toLowerCase())}`}
                    underline="none"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button
                      fullWidth
                      label={t('ammunitionDetails.showArtInformation.button')}
                      variant={EButtonVariants.contained}
                      size={EButtonSizes.small}
                    />
                  </Link>
                </Stack>
              </Grid>
              {showField('manufacturer') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.ammunitionManufacturer)}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(ammunitionDetailsData?.manufacturer)}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('productName') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.productName)}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(ammunitionDetailsData?.productName)}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('factory') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.factory)}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(ammunitionDetailsData?.factory)}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('calibreGauge') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.calibreGauge)}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(ammunitionDetailsData?.calibre)}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('functionalTypeUniqueId') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.functionalType)}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(ammunitionDetailsData?.functionalType)}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('cartridgeComposition') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.cartridgeComposition)}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(ammunitionDetailsData?.cartridgeComposition)}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('colourOfPrimerSealant') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.colourOfPrimerSealant)}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(ammunitionDetailsData?.colourOfPrimerSealant)}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('shotSize') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.shotSize)}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(ammunitionDetailsData?.shotSize)}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('munitionType') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.munitionType)}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(ammunitionDetailsData?.munitionType)}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('munitionVelocity') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.munitionVelocity)}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(ammunitionDetailsData?.munitionVelocity)}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('projectileShape') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.projectileShape)}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(ammunitionDetailsData?.projectileShape)}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('alphanumericalMarkins') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.alphanumericalMarkins)}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(ammunitionDetailsData?.alphanumericalMarkins)}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('expirationDate') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.expirationDate)}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(ammunitionDetailsData?.expirationDate)}
                    </Typography>
                  </Stack>
                </Grid>
              )}
            </AmmunitionInformationContainer>
            <AmmunitionInformationContainer
              size={{ xs: 12 }}
              container
              borderHidden={isForTransactionDetails || !showMarkings}
            >
              <Grid size={{ xs: 12 }}>
                <Stack>
                  <AmmunitionInformationTitle variant="subtitle1">
                    {t(EAmmunitionLabels.ammunitionDetailedInformation)}
                  </AmmunitionInformationTitle>
                </Stack>
              </Grid>
              {showField('fireCycleId') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.firecycleID)}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(ammunitionDetailsData?.fireCycleId)}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('lotNumber') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.lotNumber)}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(ammunitionDetailsData?.lotNumber)}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('batchNumber') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.batchNumber)}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(ammunitionDetailsData?.batchNumber)}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('state') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.ammunitionState)}
                    </Typography>
                    <Typography variant="body2">
                      <Chips
                        text={emptyFieldHolder(
                          parseDictionary(state, 'name')[ammunitionDetailsData.stateUniqueId],
                        )}
                        variant={
                          renderDataColorByValues(
                            ammunitionDetailsData.activityUniqueId,
                            ammunitionDetailsData.legalityUniqueId,
                            ammunitionDetailsData.stateUniqueId,
                          ).stateField
                        }
                      />
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('ownerName') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.currentOwner)}
                    </Typography>
                    <Typography variant="body2">
                      {!isForTransactionDetails &&
                      ammunitionDetailsData.isOwnerHyperLinkActive ? (
                        <StyledLegalLink
                          legalId={ammunitionDetailsData.ownerId}
                          legalType={getLegalEntityType(ammunitionDetailsData.ownerType)}
                          labelText={ammunitionDetailsData?.ownerName}
                          legalVisibilityUniqueId={
                            ammunitionDetailsData.ownerVisibilityUniqueId
                          }
                        />
                      ) : (
                        <Typography variant="body2">
                          {emptyFieldHolder(ammunitionDetailsData?.ownerName)}
                        </Typography>
                      )}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('keeperName') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.currentKeeper)}
                    </Typography>
                    <Typography variant="body2">
                      {!isForTransactionDetails &&
                      ammunitionDetailsData.isKeeperHyperLinkActive ? (
                        <StyledLegalLink
                          legalId={ammunitionDetailsData.keeperId}
                          legalType={getLegalEntityType(ammunitionDetailsData.keeperType)}
                          labelText={ammunitionDetailsData?.keeperName}
                          legalVisibilityUniqueId={
                            ammunitionDetailsData.keeperVisibilityUniqueId
                          }
                        />
                      ) : (
                        <Typography variant="body2">
                          {emptyFieldHolder(ammunitionDetailsData?.keeperName)}
                        </Typography>
                      )}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('productionDate') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.productionDate)}
                    </Typography>
                    <Typography variant="body2">
                      {ammunitionDetailsData?.productionDate
                        ? formatDate(ammunitionDetailsData?.productionDate, shortDateFormat)
                        : '-'}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('ammunitionExpiryDate') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.expiryDate)}
                    </Typography>
                    <Typography variant="body2">
                      {ammunitionDetailsData?.ammunitionExpiryDate
                        ? formatDate(
                            ammunitionDetailsData?.ammunitionExpiryDate,
                            shortDateFormat,
                          )
                        : '-'}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('exhibitNumber') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.exhibitNumber)}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(ammunitionDetailsData?.exhibitNumber)}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('headstampYear') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.headstampYear)}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(ammunitionDetailsData?.headstampYear)}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {isForTransactionDetails && initialTransaction && (
                <>
                  <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {transactionType === ETransactionTypes.DECLARED_IMPORTED
                          ? t(EAmmunitionLabels.importMarking)
                          : t(EAmmunitionLabels.appliedMarking)}
                      </Typography>
                      <Typography variant="body2">{emptyFieldHolder(marking)}</Typography>
                    </Stack>
                  </Grid>
                </>
              )}
              {visibleCustomFieldsData && (
                // eslint-disable-next-line react/jsx-no-useless-fragment
                <>
                  {handleCustomFieldsData()?.map((customField) => (
                    <Grid size={{ xs: 6, lg: 3 }} key={customField.customFieldUniqueId}>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {customField?.translations?.filter(
                          (translation) =>
                            selectedLanguage?.uniqueId === translation.languageUniqueId,
                        )?.[0]?.name ||
                          customField?.translations?.filter(
                            (translation) =>
                              defaultLanguage?.uniqueId === translation.languageUniqueId,
                          )?.[0]?.name}
                      </Typography>
                      <Typography variant="body2">
                        {renderCustomFieldData(customField)}
                      </Typography>
                    </Grid>
                  ))}
                </>
              )}
            </AmmunitionInformationContainer>
            {!isForTransactionDetails && (
              <AmmunitionInformationContainer size={{ xs: 12 }} container borderHidden>
                {ammunitionDetailsData.markings && showMarkings && (
                  <>
                    <Grid size={{ xs: 12 }}>
                      <Stack>
                        <AmmunitionInformationTitle variant="subtitle1">
                          {t(EAmmunitionLabels.ammunitionMarkings)}
                        </AmmunitionInformationTitle>
                      </Stack>
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAmmunitionLabels.importMarking)}
                      </Typography>
                      <Typography variant="body2">
                        {emptyFieldHolder(
                          ammunitionDetailsData.markings.find(
                            (marking) => marking.markingType === EMarkingTypes.IMPORT,
                          )?.marking,
                        )}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAmmunitionLabels.appliedMarking)}
                      </Typography>
                      <Typography variant="body2">
                        {emptyFieldHolder(
                          ammunitionDetailsData.markings.find(
                            (marking) => marking.markingType === EMarkingTypes.APPLIED,
                          )?.marking,
                        )}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAmmunitionLabels.deactivationMarking)}
                      </Typography>
                      <Typography variant="body2">
                        {emptyFieldHolder(
                          ammunitionDetailsData.markings.find(
                            (marking) => marking.markingType === EMarkingTypes.DEACTIVATION,
                          )?.marking,
                        )}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAmmunitionLabels.exportMarking)}
                      </Typography>
                      <Typography variant="body2">
                        {emptyFieldHolder(
                          ammunitionDetailsData.markings.find(
                            (marking) => marking.markingType === EMarkingTypes.EXPORT,
                          )?.marking,
                        )}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAmmunitionLabels.surplusSaleMarking)}
                      </Typography>
                      <Typography variant="body2">
                        {emptyFieldHolder(
                          ammunitionDetailsData.markings.find(
                            (marking) => marking.markingType === EMarkingTypes.SURPLUS_SALE,
                          )?.marking,
                        )}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAmmunitionLabels.yearOfTransfer)}
                      </Typography>
                      <Typography variant="body2">
                        {emptyFieldHolder(ammunitionDetailsData.yearOfTransfer)}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAmmunitionLabels.confiscationMarking)}
                      </Typography>
                      <Typography variant="body2">
                        {emptyFieldHolder(
                          ammunitionDetailsData.markings.find(
                            (marking) => marking.markingType === EMarkingTypes.CONFISCATION,
                          )?.marking,
                        )}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAmmunitionLabels.yearOfConfiscation)}
                      </Typography>
                      <Typography variant="body2">
                        {emptyFieldHolder(ammunitionDetailsData.yearOfConfiscation)}
                      </Typography>
                    </Grid>
                  </>
                )}
                {ammunitionDetailsData.markings && (
                  <Grid container size={{ xs: 12 }} sx={{ justifyContent: 'flex-end', mt: 3 }}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Button
                        fullWidth
                        label={
                          showMarkings
                            ? t('ancillaryDetails.hideAncillaryMarkings.button')
                            : t('ancillaryDetails.seeAncillaryMarkings.button')
                        }
                        variant={EButtonVariants.outlined}
                        size={EButtonSizes.small}
                        onClick={() => setShowMarkings(!showMarkings)}
                      />
                    </Grid>
                  </Grid>
                )}
              </AmmunitionInformationContainer>
            )}
          </DetailsTemplate>
        </Grid>
      )}
    </div>
  );
};

export default AmmunitionDetails;
